import { useCallback, useEffect, useRef, useState } from 'react'
import useSWR from 'swr'
import { audienceImportSessionsApi } from '@/lib/api'
import type { AudienceCreationFlowSnapshot } from '@digital-twins/shared-types'
import type { AudienceImportContextMaterialsProjection } from '@/types/components/audiences/import-session'
import type { ImportSessionUpdatedEvent } from '@/lib/services/websocket'
import {
  useImportWizardRealtimeContext,
  useImportWizardStableContext,
} from '../context/ImportWizardContext'
import { getAudienceImportContextMaterialsProjectionKey } from '../lib/cache-keys'
import { incrementImportSessionClientMetric } from '../lib/import-session-client-metrics'
import { legacyRefreshInterval } from '@/lib/realtime/realtimeKillSwitch'
import { shouldRevalidateContextMaterialsProjectionForImportSessionUpdate } from '../lib/import-session-update-events'
import {
  getRouteAckFromImportSessionEvent,
  mergeRouteAckIntoContextMaterialsProjection,
} from '../lib/import-session-deltas'

const FILE_LIST_REFRESH_REASONS = new Set([
  'context_materials_file_added',
  'context_materials_file_removed',
  'context_materials_retry_requested',
  'context_materials_indexing_updated',
  'context_materials_cleanup_completed',
])

interface UseContextMaterialsProjectionOptions {
  lastMutationRequestId?: string | null
  flowSnapshot?: AudienceCreationFlowSnapshot | null
}

export function useContextMaterialsProjection(
  sessionId: string,
  _options: UseContextMaterialsProjectionOptions = {},
) {
  const { isRealtimeConnected } = useImportWizardRealtimeContext()
  const { registerImportSessionEventHandler } = useImportWizardStableContext()
  const [filesRefreshToken, setFilesRefreshToken] = useState(0)

  const key = getAudienceImportContextMaterialsProjectionKey(sessionId)
  const query = useSWR<AudienceImportContextMaterialsProjection | null>(
    key,
    sessionId
      ? (_key: unknown, options?: { signal?: AbortSignal }) =>
          audienceImportSessionsApi.getContextMaterialsProjection(sessionId, options?.signal)
      : null,
    {
      refreshInterval: legacyRefreshInterval('IMPORT', 1500),
      revalidateOnFocus: false,
      dedupingInterval: 5000,
    },
  )

  const revalidateProjection = useCallback(
    async (source: string) => {
      const start = performance.now()
      try {
        await query.mutate()
      } finally {
        incrementImportSessionClientMetric(
          'context_materials_projection_revalidate',
          {
            sessionId,
            hook: 'context_materials',
            source,
            durationMs: Math.round(performance.now() - start),
          },
        )
      }
    },
    [query, sessionId],
  )

  const handlerRef = useRef<(event: ImportSessionUpdatedEvent) => void>(() => {})
  handlerRef.current = (event: ImportSessionUpdatedEvent) => {
    if (FILE_LIST_REFRESH_REASONS.has(event.reason)) {
      setFilesRefreshToken((current) => current + 1)
    }
    if (!shouldRevalidateContextMaterialsProjectionForImportSessionUpdate(event)) {
      incrementImportSessionClientMetric(
        'context_materials_projection_revalidate_skipped',
        {
          sessionId,
          hook: 'context_materials',
          source: event.reason,
        },
      )
      return
    }
    const ack = getRouteAckFromImportSessionEvent(event)
    if (ack) {
      void query.mutate(
        (current) => {
          const merged = mergeRouteAckIntoContextMaterialsProjection(
            current ?? null,
            ack,
          )
          return merged ?? current
        },
        { revalidate: false },
      )
      return
    }
    void revalidateProjection(`websocket:${event.reason}`)
  }
  const stableHandler = useCallback((event: ImportSessionUpdatedEvent) => {
    handlerRef.current(event)
  }, [])

  useEffect(() => {
    return registerImportSessionEventHandler(stableHandler)
  }, [registerImportSessionEventHandler, stableHandler])

  return {
    projection: query.data ?? null,
    error: query.error as Error | undefined,
    isLoading: query.isLoading,
    isValidating: query.isValidating,
    isRealtimeConnected,
    filesRefreshToken,
    mutate: query.mutate,
  }
}
